import { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_07dbfad7-7b72-440c-99bb-6448c036d53f/artifacts/j0yj8a0k_This%20logo.png";
const TAGLINE = "Heritage by Jabalpuriyas, for Jabalpuriyas, and of Jabalpuriyas.";

/**
 * JabalipuraanLogo - Reusable logo component with magnify interaction
 * 
 * Desktop: Hover shows 3x centered overlay with logo + tagline
 * Mobile: Tap opens modal with logo + tagline
 * Both: Blurred + darkened backdrop for contrast
 */
export const JabalipuraanLogo = ({ 
  className = '', 
  size = 'md', 
  showText = false,
  textClassName = '',
  linkTo = '/'
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const overlayRef = useRef(null);

  // Size mappings for the logo
  const sizeClasses = {
    sm: 'h-8 w-auto',
    md: 'h-10 md:h-12 w-auto',
    lg: 'h-12 md:h-16 w-auto',
    xl: 'h-16 md:h-24 w-auto'
  };

  // Size values in pixels for 3x calculation
  const sizePixels = {
    sm: 32,
    md: 48,
    lg: 64,
    xl: 96
  };

  const logoSizeClass = sizeClasses[size] || sizeClasses.md;
  const baseSize = sizePixels[size] || sizePixels.md;
  const enlargedSize = baseSize * 3; // 3x size for overlay

  // Detect touch device
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouch();
  }, []);

  // Handle mouse enter (desktop hover)
  const handleMouseEnter = useCallback(() => {
    if (isTouchDevice) return;
    
    // Clear any pending close timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    setIsHovering(true);
    setIsOverlayOpen(true);
  }, [isTouchDevice]);

  // Handle mouse leave (desktop)
  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice) return;
    
    setIsHovering(false);
    
    // Add small delay before closing to avoid flicker
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOverlayOpen(false);
    }, 150);
  }, [isTouchDevice]);

  // Handle overlay mouse enter (keep open when hovering overlay)
  const handleOverlayMouseEnter = useCallback(() => {
    if (isTouchDevice) return;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovering(true);
  }, [isTouchDevice]);

  // Handle overlay mouse leave
  const handleOverlayMouseLeave = useCallback(() => {
    if (isTouchDevice) return;
    
    setIsHovering(false);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOverlayOpen(false);
    }, 150);
  }, [isTouchDevice]);

  // Handle click/tap (mobile)
  const handleClick = useCallback((e) => {
    if (isTouchDevice) {
      e.preventDefault();
      e.stopPropagation();
      setIsOverlayOpen(true);
    }
    // On desktop, let the link work normally (navigation happens on click, not hover)
  }, [isTouchDevice]);

  // Close overlay (for mobile X button)
  const closeOverlay = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsOverlayOpen(false);
  }, []);

  // Handle backdrop click (both mobile and desktop - clicking outside closes modal)
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      closeOverlay();
    }
  }, [closeOverlay]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closeOverlay();
    }
  }, [closeOverlay]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Focus trap for accessibility when overlay is open
  useEffect(() => {
    if (isOverlayOpen && overlayRef.current) {
      overlayRef.current.focus();
    }
  }, [isOverlayOpen]);

  return (
    <>
      {/* Logo wrapper */}
      <a
        href={linkTo}
        className={`jabalipuraan-logo-wrapper inline-flex items-center gap-3 ${className}`}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label="Jabalipuraan - View enlarged logo"
        data-testid="jabalipuraan-logo"
      >
        <img 
          src={LOGO_URL} 
          alt="Jabalipuraan Logo"
          className={`jabalipuraan-logo ${logoSizeClass}`}
          draggable="false"
        />
        {showText && (
          <span className={`font-heading text-xl md:text-2xl font-semibold ${textClassName}`}>
            Jabalipuraan
          </span>
        )}
      </a>

      {/* Overlay - shows on hover (desktop) or tap (mobile) */}
      {isOverlayOpen && (
        <div 
          className="jabalipuraan-overlay-backdrop"
          onClick={handleBackdropClick}
          onMouseEnter={handleOverlayMouseEnter}
          onMouseLeave={handleOverlayMouseLeave}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label="Jabalipuraan Logo - Enlarged View"
          tabIndex={-1}
          ref={overlayRef}
        >
          {/* Centered card with logo and tagline */}
          <div 
            className="jabalipuraan-overlay-card"
            onMouseEnter={handleOverlayMouseEnter}
            onMouseLeave={handleOverlayMouseLeave}
          >
            {/* Close button - always visible for accessibility, styled differently on mobile vs desktop */}
            <button
              onClick={closeOverlay}
              className={`jabalipuraan-overlay-close ${isTouchDevice ? 'touch-device' : 'desktop-device'}`}
              aria-label="Close logo view"
              data-testid="logo-overlay-close"
            >
              <X className="h-6 w-6" />
            </button>
            
            {/* Enlarged logo - 3x size on desktop, appropriate size on mobile */}
            <img 
              src={LOGO_URL} 
              alt="Jabalipuraan Logo - Enlarged"
              className="jabalipuraan-overlay-logo"
              style={{ 
                height: isTouchDevice ? '200px' : `${enlargedSize}px`,
                width: 'auto'
              }}
            />
            
            {/* Title */}
            <h2 className="jabalipuraan-overlay-title">
              Jabalipuraan
            </h2>
            
            {/* Tagline */}
            <p className="jabalipuraan-overlay-tagline">
              {TAGLINE}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default JabalipuraanLogo;
